import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query, doc, deleteDoc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from "../components/Loader";

export default function Appointments() {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [serviceFilter, setServiceFilter] = useState("All");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [editingAppt, setEditingAppt] = useState(null); // For Edit Modal
    const [isAdding, setIsAdding] = useState(false);
    const [newAppt, setNewAppt] = useState({
        name: "",
        mobile: "",
        serviceType: "General Service",
        date: "",
        address: "",
        remarks: ""
    });
    const itemsPerPage = 10;

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, serviceFilter, dateFrom, dateTo]);

    useEffect(() => {
        const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const bookings = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            // Auto-generate Ticket ID for old records if missing
            bookings.forEach(async (b) => {
                if (!b.ticketId) {
                    const date = b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date();
                    const yyyy = date.getFullYear();
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const dd = String(date.getDate()).padStart(2, "0");
                    const random = Math.floor(1000 + Math.random() * 9000);
                    let prefix = "B";
                    if (b.serviceType) {
                        prefix = b.serviceType === "General Service" ? "S" : b.serviceType.charAt(0).toUpperCase();
                    }
                    const newTicketId = `${prefix}-${yyyy}${mm}${dd}-${random}`;

                    try {
                        await updateDoc(doc(db, "bookings", b.id), { ticketId: newTicketId });
                    } catch (e) {
                        console.error("Error auto-generating ticket ID", e);
                    }
                }
            });

            setAppointments(bookings);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const date = new Date();
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const dd = String(date.getDate()).padStart(2, "0");
            const random = Math.floor(1000 + Math.random() * 9000);
            let prefix = "B";
            if (newAppt.serviceType) {
                prefix = newAppt.serviceType === "General Service" ? "S" : newAppt.serviceType.charAt(0).toUpperCase();
            }
            const newTicketId = `${prefix}-${yyyy}${mm}${dd}-${random}`;

            await addDoc(collection(db, "bookings"), {
                ...newAppt,
                status: "Pending",
                ticketId: newTicketId,
                createdAt: serverTimestamp(),
            });
            setIsAdding(false);
            setNewAppt({
                name: "",
                mobile: "",
                serviceType: "General Service",
                date: "",
                address: "",
                remarks: ""
            });
        } catch (err) {
            console.error("Error creating appointment:", err);
            alert("Failed to create appointment");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this appointment?")) {
            try {
                await deleteDoc(doc(db, "bookings", id));
            } catch (err) {
                console.error("Error deleting appointment:", err);
            }
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await updateDoc(doc(db, "bookings", id), { status: newStatus });
        } catch (err) {
            console.error("Error updating status:", err);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!editingAppt) return;
        try {
            await updateDoc(doc(db, "bookings", editingAppt.id), {
                name: editingAppt.name,
                mobile: editingAppt.mobile,
                serviceType: editingAppt.serviceType,
                date: editingAppt.date,
                address: editingAppt.address,
                status: editingAppt.status,
                remarks: editingAppt.remarks || ""
            });
            setEditingAppt(null);
        } catch (err) {
            console.error("Error updating appointment:", err);
            alert("Failed to update appointment");
        }
    };

    const handleExport = () => {
        if (filteredAppointments.length === 0) {
            alert("No data to export");
            return;
        }

        const dataToExport = filteredAppointments.map(appt => ({
            "Date Requested": appt.createdAt?.seconds ? new Date(appt.createdAt.seconds * 1000).toLocaleString() : "N/A",
            "Ticket ID": appt.ticketId || "N/A",
            "Name": appt.name,
            "Mobile": appt.mobile,
            "Service Type": appt.serviceType,
            "Preferred Date": appt.date,
            "Address": appt.address,
            "Status": appt.status,
            "Remarks": appt.remarks || ""
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Appointments");

        // Buffer
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });

        saveAs(data, `Appointments_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Filter Logic for Counts (Apply all filters except Status)
    const getFilteredForCounts = () => {
        return appointments.filter((appt) => {
            const matchesSearch =
                (appt.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (appt.mobile?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (appt.ticketId?.toLowerCase() || "").includes(searchTerm.toLowerCase());

            const matchesService = serviceFilter === "All" || appt.serviceType === serviceFilter;

            let matchesDate = true;
            if (dateFrom || dateTo) {
                const apptDate = appt.date;
                if (dateFrom && apptDate < dateFrom) matchesDate = false;
                if (dateTo && apptDate > dateTo) matchesDate = false;
            }

            return matchesSearch && matchesService && matchesDate;
        });
    };

    const filteredForCounts = getFilteredForCounts();

    const counts = {
        All: filteredForCounts.length,
        Pending: 0,
        Confirmed: 0,
        Completed: 0,
        Cancelled: 0
    };

    filteredForCounts.forEach(appt => {
        if (counts[appt.status] !== undefined) {
            counts[appt.status]++;
        }
    });

    // Final Filter Logic (Include Status)
    const filteredAppointments = filteredForCounts.filter(appt => statusFilter === "All" || appt.status === statusFilter);

    // Pagination Logic
    const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAppointments.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    if (loading) {
        return (<Loader />);
    }

    return (
        <div className="container-fluid mt-4 px-4">
            <h3 className="mb-4">Appointments / Bookings</h3>

            {/* Status Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${statusFilter === 'All' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('All')}
                    >
                        All ({counts.All})
                    </button>
                </li>
                {['Pending', 'Confirmed', 'Completed', 'Cancelled'].map(status => (
                    <li className="nav-item" key={status}>
                        <button
                            className={`nav-link ${statusFilter === status ? 'active' : ''}`}
                            onClick={() => setStatusFilter(status)}
                        >
                            {status} ({counts[status] || 0})
                        </button>
                    </li>
                ))}
            </ul>

            <div className="row mb-3 g-2">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={serviceFilter}
                        onChange={(e) => setServiceFilter(e.target.value)}
                    >
                        <option value="All">All Service Types</option>
                        <option value="General Service">General Service</option>
                        <option value="Installation">Installation</option>
                        <option value="Gas Filling">Gas Filling</option>
                        <option value="Repair / Breakdown">Repair / Breakdown</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        title="From Date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                    />
                </div>
                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        title="To Date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                    />
                </div>
                <div className="col-md-2 d-flex gap-2">
                    <button className="btn btn-primary w-100" onClick={() => setIsAdding(true)} title="Create Appointment">
                        <i className="bi bi-plus-lg"></i>
                    </button>
                    <button className="btn btn-success w-100" onClick={handleExport} title="Export to Excel">
                        <i className="bi bi-file-earmark-excel"></i>
                    </button>
                </div>
            </div>

            {loading ? (
                <p>Loading bookings...</p>
            ) : filteredAppointments.length === 0 ? (
                <p>No bookings found matching criteria.</p>
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th>Date Requested</th>
                                    <th>Ticket ID</th>
                                    <th>Name</th>
                                    <th>Mobile</th>
                                    <th>Service Type</th>
                                    <th>Preferred Date</th>
                                    <th>Address</th>
                                    <th>Remarks</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((appt) => (
                                    <tr key={appt.id}>
                                        <td style={{ fontSize: '0.85rem' }}>
                                            {appt.createdAt?.seconds
                                                ? new Date(appt.createdAt.seconds * 1000).toLocaleString()
                                                : "Just now"}
                                        </td>
                                        <td><span className="badge bg-secondary">{appt.ticketId || "N/A"}</span></td>
                                        <td>{appt.name}</td>
                                        <td>{appt.mobile}</td>
                                        <td>
                                            <span className="badge bg-info text-dark">{appt.serviceType}</span>
                                        </td>
                                        <td>{appt.date}</td>
                                        <td style={{ maxWidth: '200px', fontSize: '0.9rem' }}>{appt.address}</td>
                                        <td style={{ maxWidth: '150px', fontSize: '0.85rem' }}>{appt.remarks || "-"}</td>
                                        <td>
                                            <select
                                                className={`form-select form-select-sm ${appt.status === "Completed"
                                                    ? "bg-success text-white"
                                                    : appt.status === "Pending"
                                                        ? "bg-warning"
                                                        : appt.status === "Cancelled"
                                                            ? "bg-danger text-white"
                                                            : "bg-primary text-white"
                                                    }`}
                                                value={appt.status || "Pending"}
                                                onChange={(e) => handleStatusChange(appt.id, e.target.value)}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Confirmed">Confirmed</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </td>
                                        <td>
                                            <div className="btn-group">
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => setEditingAppt(appt)}
                                                    title="Edit Appointment"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(appt.id)}
                                                    title="Delete Appointment"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <nav>
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                                </li>
                                {[...Array(totalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                                </li>
                            </ul>
                        </nav>
                    )}

                    {/* Edit Modal */}
                    {editingAppt && (
                        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Edit Booking ({editingAppt.ticketId})</h5>
                                        <button type="button" className="btn-close" onClick={() => setEditingAppt(null)}></button>
                                    </div>
                                    <form onSubmit={handleUpdate}>
                                        <div className="modal-body">
                                            <div className="mb-3">
                                                <label className="form-label">Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editingAppt.name}
                                                    onChange={e => setEditingAppt({ ...editingAppt, name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Mobile</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editingAppt.mobile}
                                                    onChange={e => setEditingAppt({ ...editingAppt, mobile: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Service Type</label>
                                                <select
                                                    className="form-select"
                                                    value={editingAppt.serviceType}
                                                    onChange={e => setEditingAppt({ ...editingAppt, serviceType: e.target.value })}
                                                >
                                                    <option>General Service</option>
                                                    <option>Installation</option>
                                                    <option>Gas Filling</option>
                                                    <option>Repair / Breakdown</option>
                                                    <option>Other</option>
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Preferred Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={editingAppt.date}
                                                    onChange={e => setEditingAppt({ ...editingAppt, date: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Address</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={editingAppt.address}
                                                    onChange={e => setEditingAppt({ ...editingAppt, address: e.target.value })}
                                                    required
                                                ></textarea>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    value={editingAppt.status}
                                                    onChange={e => setEditingAppt({ ...editingAppt, status: e.target.value })}
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Confirmed">Confirmed</option>
                                                    <option value="Completed">Completed</option>
                                                    <option value="Cancelled">Cancelled</option>
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Remarks</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={editingAppt.remarks || ""}
                                                    onChange={e => setEditingAppt({ ...editingAppt, remarks: e.target.value })}
                                                    placeholder="Add internal notes here..."
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" onClick={() => setEditingAppt(null)}>Cancel</button>
                                            <button type="submit" className="btn btn-primary">Save Changes</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Add Modal */}
                    {isAdding && (
                        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Create Appointment</h5>
                                        <button type="button" className="btn-close" onClick={() => setIsAdding(false)}></button>
                                    </div>
                                    <form onSubmit={handleCreate}>
                                        <div className="modal-body">
                                            <div className="mb-3">
                                                <label className="form-label">Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newAppt.name}
                                                    onChange={e => setNewAppt({ ...newAppt, name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Mobile</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newAppt.mobile}
                                                    onChange={e => setNewAppt({ ...newAppt, mobile: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Service Type</label>
                                                <select
                                                    className="form-select"
                                                    value={newAppt.serviceType}
                                                    onChange={e => setNewAppt({ ...newAppt, serviceType: e.target.value })}
                                                >
                                                    <option>General Service</option>
                                                    <option>Installation</option>
                                                    <option>Gas Filling</option>
                                                    <option>Repair / Breakdown</option>
                                                    <option>Other</option>
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Preferred Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={newAppt.date}
                                                    onChange={e => setNewAppt({ ...newAppt, date: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Address</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={newAppt.address}
                                                    onChange={e => setNewAppt({ ...newAppt, address: e.target.value })}
                                                    required
                                                ></textarea>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Remarks</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={newAppt.remarks}
                                                    onChange={e => setNewAppt({ ...newAppt, remarks: e.target.value })}
                                                    placeholder="Add internal notes here..."
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-secondary" onClick={() => setIsAdding(false)}>Cancel</button>
                                            <button type="submit" className="btn btn-primary">Create Booking</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
